import React, { useCallback, useEffect, useState } from 'react';
import { Splitter, Spin, Button, Typography, Tabs, Tag, Modal, Form, Select, Alert } from 'antd';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import moment from 'moment';
import './view2.css';

import FlowCanvas from '../flow-canvas/FlowCanvas';
import '../flow-canvas/FlowCanvas.css';
import FlowEnrollmentBoard from '../flow-enrollment/FlowEnrollmentBoard';
import ModuleDetailPanel from './ModuleDetailPanel';
import TemplateMetaFields from './TemplateMetaFields';
import { CATEGORY_LABELS } from '../templates/categories';
import useModuleGroupStore from '../../stores/useModuleGroupStore';
import { getAllCompanies } from '../../reducers/company/resources';
import mixpanel from '../../utils/mixpanel';

const { Title, Text } = Typography;

function ModuleGroupView2(props) {
    const { modulegroupId } = props.match.params;

    const {
        loading,
        moduleGroup,
        modules,
        selectedModuleId,
        fetchModuleGroup,
        setSelectedModuleId,
        saveFlowMeta,
        updateTemplateMeta,
        updateCompany,
    } = useModuleGroupStore();

    const [templateForm] = Form.useForm();
    const [companyForm] = Form.useForm();
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
    const [isSavingTemplate, setIsSavingTemplate] = useState(false);
    const [isSavingCompany, setIsSavingCompany] = useState(false);
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        if (modulegroupId) fetchModuleGroup(modulegroupId);
    }, [modulegroupId, fetchModuleGroup]);

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await getAllCompanies();
                setCompanies(res.data || []);
            } catch (err) {
                console.error('Failed to fetch companies', err);
            }
        };
        fetchCompanies();
    }, []);

    const handleNodeSelect = useCallback((moduleId) => {
        setSelectedModuleId(moduleId);
    }, [setSelectedModuleId]);

    const handleGoBack = () => {
        if (props.history) props.history.push('/module-group');
    };

    const openTemplateModal = () => {
        templateForm.setFieldsValue({
            is_template: !!moduleGroup?.is_template,
            category: moduleGroup?.category || undefined,
        });
        setIsTemplateModalOpen(true);
    };

    const handleTemplateSubmit = async (values) => {
        setIsSavingTemplate(true);
        try {
            await updateTemplateMeta({
                is_template: !!values.is_template,
                category: values.is_template ? values.category : null,
            });
            mixpanel.track('module group:set template flag', {
                data: {
                    modulegroup_id: moduleGroup?.id,
                    is_template: !!values.is_template,
                    category: values.is_template ? values.category : null,
                },
            });
            setIsTemplateModalOpen(false);
        } finally {
            setIsSavingTemplate(false);
        }
    };

    const openCompanyModal = () => {
        companyForm.setFieldsValue({
            company_id: moduleGroup?.company?.id || moduleGroup?.company_id || undefined,
        });
        setIsCompanyModalOpen(true);
    };

    const handleCompanySubmit = async (values) => {
        setIsSavingCompany(true);
        try {
            await updateCompany(values.company_id || null);
            mixpanel.track('module group:update company', {
                data: {
                    modulegroup_id: moduleGroup?.id,
                    company_id: values.company_id || null,
                },
            });
            setIsCompanyModalOpen(false);
        } finally {
            setIsSavingCompany(false);
        }
    };

    const hasCompany = !!(moduleGroup?.company || moduleGroup?.company_id);

    if (loading && !moduleGroup) {
        return (
            <div className="mg-v2-loading">
                <Spin size="large" tip="Loading module group..." />
            </div>
        );
    }

    return (
        <div className="module-group-v2">
            {moduleGroup?.deleted_at && (
                <Alert
                    type="warning"
                    showIcon
                    banner
                    message={(
                        <span>
                            This sequence was deleted {moment(moduleGroup.deleted_at).fromNow()}. It is read-only — historical
                            enrollments are preserved, but new enrollments and edits are disabled.
                        </span>
                    )}
                />
            )}
            <div className="mg-v2-header">
                <div className="mg-v2-header-left">
                    <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleGoBack} />
                    {moduleGroup && (
                        <>
                            {moduleGroup.company ? (
                                <Tag
                                    style={{ cursor: 'pointer', marginRight: 0 }}
                                    onClick={openCompanyModal}
                                >
                                    {moduleGroup.company.name}
                                </Tag>
                            ) : (
                                !moduleGroup.is_template && (
                                    <Button type="dashed" size="small" onClick={openCompanyModal}>
                                        Assign company
                                    </Button>
                                )
                            )}
                            <Title level={5} style={{ margin: 0 }}>
                                {moduleGroup.name}
                            </Title>
                            {moduleGroup.is_template && (
                                <Tag
                                    color="gold"
                                    style={{ marginLeft: 4, cursor: 'pointer' }}
                                    onClick={openTemplateModal}
                                >
                                    Template
                                    {moduleGroup.category && (
                                        <> — {CATEGORY_LABELS[moduleGroup.category] || moduleGroup.category}</>
                                    )}
                                </Tag>
                            )}
                            {moduleGroup.description && (
                                <Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>
                                    — {moduleGroup.description}
                                </Text>
                            )}
                        </>
                    )}
                </div>
                <div className="mg-v2-header-right">
                    {moduleGroup && !hasCompany && (
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={openTemplateModal}
                        >
                            {moduleGroup.is_template ? 'Edit template' : 'Mark as template'}
                        </Button>
                    )}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        {modules.length} module{modules.length !== 1 ? 's' : ''}
                    </Text>
                </div>
            </div>

            <Modal
                title="Template settings"
                open={isTemplateModalOpen}
                onCancel={() => setIsTemplateModalOpen(false)}
                onOk={() => templateForm.submit()}
                okText="Save"
                okButtonProps={{ loading: isSavingTemplate }}
                destroyOnClose
            >
                <Form
                    layout="vertical"
                    form={templateForm}
                    onFinish={handleTemplateSubmit}
                    preserve={false}
                >
                    <TemplateMetaFields form={templateForm} />
                </Form>
            </Modal>

            <Modal
                title="Company assignment"
                open={isCompanyModalOpen}
                onCancel={() => setIsCompanyModalOpen(false)}
                onOk={() => companyForm.submit()}
                okText="Save"
                okButtonProps={{ loading: isSavingCompany }}
                destroyOnClose
            >
                <Form
                    layout="vertical"
                    form={companyForm}
                    onFinish={handleCompanySubmit}
                    preserve={false}
                >
                    <Form.Item
                        name="company_id"
                        label="Company"
                        extra="Clear the field to remove the assignment"
                    >
                        <Select
                            placeholder="Select a company"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            options={companies.map(c => ({ value: c.id, label: c.name }))}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <Tabs
                defaultActiveKey="flow"
                style={{ height: 'calc(100% - 48px)' }}
                tabBarStyle={{ padding: '0 16px', margin: 0 }}
                items={[
                    {
                        key: 'flow',
                        label: 'Flow',
                        children: (
                            <Splitter style={{ height: '100%' }}>
                                <Splitter.Panel defaultSize="70%" min="70%" max="70%">
                                    <FlowCanvas
                                        modules={modules}
                                        startModuleId={moduleGroup ? moduleGroup.module_id : null}
                                        savedFlowMeta={moduleGroup?.flow_meta}
                                        selectedModuleId={selectedModuleId}
                                        onNodeSelect={handleNodeSelect}
                                        onSaveLayout={saveFlowMeta}
                                    />
                                </Splitter.Panel>
                                <Splitter.Panel defaultSize="30%">
                                    <div className="mg-v2-detail-panel">
                                        <ModuleDetailPanel />
                                    </div>
                                </Splitter.Panel>
                            </Splitter>
                        ),
                    },
                    {
                        key: 'board',
                        label: 'Board',
                        children: (
                            <div style={{ height: '100%', overflow: 'auto' }}>
                                <FlowEnrollmentBoard modulegroupId={modulegroupId} />
                            </div>
                        ),
                    },
                ]}
            />
        </div>
    );
}

export default ModuleGroupView2;
